import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from '@/components/ui/command';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Package, Search, Plus, Pencil, FileText, ArrowRightLeft, Check, ChevronsUpDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  fetchInventory,
  createInventory,
  updateInventory,
  deleteInventory,
  fetchItems,
  createItem,
  updateItem,
  fetchBranches,
  fetchSuppliers,
  fetchSupplierInvoicesBySupplierId,
  fetchSettings,
  transferInventory,
} from '@/api';
import type { Branch, Supplier } from '@/types/branch';
import { useAuth } from '@/contexts/AuthContext';

interface InventoryRaw {
  id: string;
  item_id: string;
  branch_id: string | null;
  quantity: number;
  supplier_id: string | null;
}
interface ItemRaw {
  id: string;
  name: string;
  sku: string;
  buying_price: number;
  selling_price: number;
  vat_enabled?: number;
  barcode?: string;
  imeis?: string;
}
interface InventoryItem extends InventoryRaw, ItemRaw {
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
}

export default function InventoryPage() {
  useEffect(() => {
    document.title = 'Inventory - Vixalor';
  }, []);
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const userBranchId = user?.branch_id;

  const [rawInventory, setRawInventory] = useState<InventoryRaw[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [items, setItems] = useState<ItemRaw[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierInvoices, setSupplierInvoices] = useState<any[]>([]);
  const [vatPercentage, setVatPercentage] = useState<number>(0);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState<'all' | string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'in-stock' | 'low-stock' | 'out-of-stock'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState({
    item_id: '',
    item_name: '',
    item_sku: '',
    isCustomItem: false,
    branch_id: '',
    quantity: '',
    buying_price: '',
    selling_price: '',
    supplier_id: '',
    vat_enabled: false,
    barcode: '',
    imeis: '',
    hasImei: false,
    // Invoice options
    invoice_action: 'create', // 'create' or 'add_to_existing'
    custom_invoice_number: '',
    existing_invoice_id: '',
  });
  const [transferForm, setTransferForm] = useState({
    fromBranch: '',
    toBranch: '',
    itemId: '',
    quantity: '',
  });
  const [transferPopovers, setTransferPopovers] = useState({
    fromBranch: false,
    toBranch: false,
    item: false,
  });
  const [avgCostForButton, setAvgCostForButton] = useState<number>(0);

  const getStatus = (q: number): 'In Stock' | 'Low Stock' | 'Out of Stock' => {
    if (q === 0) return 'Out of Stock';
    if (q <= 10) return 'Low Stock';
    return 'In Stock';
  };

  const transformItem = (item: any): ItemRaw => {
    const buyingPrice = parseFloat(item.buying_price);
    const sellingPrice = parseFloat(item.selling_price);
    return {
      id: item.id,
      name: item.name,
      sku: item.sku,
      buying_price: isNaN(buyingPrice) ? 0 : buyingPrice,
      selling_price: isNaN(sellingPrice) ? 0 : sellingPrice,
      vat_enabled: item.vat_enabled !== undefined ? Number(item.vat_enabled) : 0,
      barcode: item.barcode || '',
      imeis: item.imeis || '',
    };
  };

  const enrich = (invRows: InventoryRaw[], itemRows: ItemRaw[]): InventoryItem[] =>
    invRows.map(i => {
      const it = itemRows.find(x => x.id === i.item_id);
      return {
        ...i,
        name: it?.name || '',
        sku: it?.sku || '',
        buying_price: it?.buying_price || 0,
        selling_price: it?.selling_price || 0,
        vat_enabled: it?.vat_enabled || 0,
        barcode: it?.barcode || '',
        imeis: it?.imeis || '',
        status: getStatus(i.quantity),
      };
    });

  const loadData = async () => {
    try {
      const [invRes, itemsRes, branchesRes, suppliersRes, settingsRes] = await Promise.all([
        fetchInventory(),
        fetchItems(),
        fetchBranches(),
        fetchSuppliers(),
        fetchSettings(),
      ]);

      const filteredInventory = isAdmin
        ? invRes.data
        : invRes.data.filter((inv: InventoryRaw) => inv.branch_id === userBranchId);

      setRawInventory(filteredInventory);
      setItems(itemsRes.data.map(transformItem));
      setBranches(branchesRes.data);
      setSuppliers(suppliersRes.data);
      setInventory(enrich(filteredInventory, itemsRes.data));
      setVatPercentage(settingsRes.data.vat_percentage || 0);

      if (!isAdmin && userBranchId) {
        setBranchFilter(userBranchId);
      }
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    loadData();
  }, [isAdmin, userBranchId]);

  const filtered = inventory.filter(r => {
    const byBranch = branchFilter === 'all' || r.branch_id === branchFilter;
    const byStatus = filterStatus === 'all' ||
      (filterStatus === 'in-stock' && r.status === 'In Stock') ||
      (filterStatus === 'low-stock' && r.status === 'Low Stock') ||
      (filterStatus === 'out-of-stock' && r.status === 'Out of Stock');
    const bySearch = r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.barcode && r.barcode.toLowerCase().includes(searchQuery.toLowerCase()));
    return byBranch && byStatus && bySearch;
  });

  const selectedItem = items.find(i => i.id === form.item_id);

  const avgCost = useMemo(() => {
    if (!form.item_id || !form.quantity || !form.buying_price) {
      return 0;
    }

    const prev = rawInventory.filter(i => i.item_id === form.item_id);
    const totalPrevQty = prev.reduce((s, r) => s + r.quantity, 0);
    const prevItem = items.find(i => i.id === form.item_id);
    const prevBuyingPrice = prevItem ? prevItem.buying_price : 0;

    const prevTotalCost = prevBuyingPrice * totalPrevQty;
    const newQty = parseInt(form.quantity, 10);
    const newBuyingPrice = parseFloat(form.buying_price);
    const newTotalCost = newQty * newBuyingPrice;

    const totalQty = totalPrevQty + newQty;
    const calculatedAvgCost = totalQty ? (prevTotalCost + newTotalCost) / totalQty : newBuyingPrice;

    setAvgCostForButton(calculatedAvgCost);

    return calculatedAvgCost;
  }, [form.item_id, form.quantity, form.buying_price, rawInventory, items]);

  const sellingPriceWithVat = useMemo(() => {
    if (!form.selling_price) return 0;
    const sellingPrice = parseFloat(form.selling_price);
    const isVatEnabled = form.isCustomItem ? form.vat_enabled : (selectedItem?.vat_enabled === 1);
    if (!isVatEnabled || vatPercentage === 0) return sellingPrice;
    const vatAmount = sellingPrice * (vatPercentage / 100);
    return (sellingPrice + vatAmount).toFixed(2);
  }, [form.selling_price, form.isCustomItem, form.vat_enabled, selectedItem, vatPercentage]);

  // NOTE: Automatic selling price suggestion removed - user should manually set selling price

  // Fetch supplier invoices when supplier changes
  useEffect(() => {
    const fetchInvoices = async () => {
      if (form.supplier_id && form.invoice_action === 'add_to_existing') {
        try {
          const response = await fetchSupplierInvoicesBySupplierId(form.supplier_id, 'pending');
          setSupplierInvoices(response.data);
        } catch (error) {
          console.error('Failed to fetch supplier invoices:', error);
          setSupplierInvoices([]);
        }
      } else {
        setSupplierInvoices([]);
      }
    };
    fetchInvoices();
  }, [form.supplier_id, form.invoice_action]);

  const openDialog = (row?: InventoryItem) => {
    if (!isAdmin) return;
    if (row) {
      setEditing(row);
      setForm({
        item_id: row.item_id,
        item_name: row.name,
        item_sku: row.sku,
        isCustomItem: false,
        branch_id: row.branch_id || '',
        quantity: row.quantity.toString(),
        buying_price: row.buying_price != null ? Number(row.buying_price).toFixed(2) : '0.00',
        selling_price: row.selling_price != null ? Number(row.selling_price).toFixed(2) : '0.00',
        supplier_id: row.supplier_id || '',
        vat_enabled: !!row.vat_enabled,
        barcode: row.barcode || '',
        imeis: row.imeis || '',
        hasImei: !!row.imeis,
      });
    } else {
      setEditing(null);
      setForm({
        item_id: '',
        item_name: '',
        item_sku: '',
        isCustomItem: false,
        branch_id: '',
        quantity: '',
        buying_price: '',
        selling_price: '',
        supplier_id: '',
        vat_enabled: false,
        barcode: '',
        imeis: '',
        hasImei: false,
        // Invoice options
        invoice_action: 'create',
        custom_invoice_number: '',
        existing_invoice_id: '',
      });
    }
    setDialogOpen(true);
    setItemSearchQuery('');
  };

  // Helper functions for transfer dialog
  const getAvailableItemsForTransfer = () => {
    if (!transferForm.fromBranch) return [];
    
    return filtered.filter(item => 
      item.branch_id === transferForm.fromBranch && item.quantity > 0
    );
  };

  const getSelectedItemQuantity = () => {
    if (!transferForm.itemId || !transferForm.fromBranch) return 0;
    
    const item = filtered.find(item => 
      item.item_id === transferForm.itemId && item.branch_id === transferForm.fromBranch
    );
    
    return item ? item.quantity : 0;
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!transferForm.fromBranch || !transferForm.toBranch || !transferForm.itemId || !transferForm.quantity) {
      toast({ title: 'Error', description: 'All fields are required', variant: 'destructive' });
      return;
    }

    const quantity = parseInt(transferForm.quantity);
    const maxQuantity = getSelectedItemQuantity();
    
    if (quantity <= 0 || quantity > maxQuantity) {
      toast({ title: 'Error', description: `Quantity must be between 1 and ${maxQuantity}`, variant: 'destructive' });
      return;
    }

    try {
      // Call transfer API
      await transferInventory({
        fromBranch: transferForm.fromBranch,
        toBranch: transferForm.toBranch,
        itemId: transferForm.itemId,
        quantity: quantity
      });

      toast({ title: 'Success', description: `Successfully transferred ${quantity} items` });
      
      // Refresh inventory data
      await loadData();
      
      // Reset form and close dialog
      setTransferForm({
        fromBranch: '',
        toBranch: '',
        itemId: '',
        quantity: '',
      });
      setTransferDialogOpen(false);
      
    } catch (error: any) {
      console.error('Transfer error:', error);
      toast({ title: 'Error', description: error.message || 'Transfer failed', variant: 'destructive' });
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!form.branch_id || !form.quantity || !form.buying_price || !form.selling_price || !form.supplier_id) {
      toast({ title: 'Error', description: 'Required fields: branch, quantity, buying price, selling price, supplier', variant: 'destructive' });
      return;
    }

    try {
      let itemId = form.item_id;
      const effectiveQuantity = form.hasImei ? 1 : parseInt(form.quantity, 10);

      if (form.isCustomItem) {
        const imeisArray = form.hasImei && form.imeis ? form.imeis.split(',').map(s => s.trim()).filter(s => s) : null;
        const itemPayload = {
          name: form.item_name,
          sku: form.item_sku,
          buying_price: parseFloat(form.buying_price),
          selling_price: parseFloat(form.selling_price),
          vat_enabled: form.vat_enabled ? 1 : 0,
          barcode: form.barcode || null,
          imeis: imeisArray ? JSON.stringify(imeisArray) : null,
          quantity: effectiveQuantity,
        };
        const itemRes = await createItem(itemPayload);
        itemId = itemRes.data.id;
        setItems([...items, transformItem(itemRes.data)]);
      } else {
        const imeisArray = form.hasImei && form.imeis ? form.imeis.split(',').map(s => s.trim()).filter(s => s) : null;
        const updateItemPayload = {
          buying_price: parseFloat(form.buying_price),
          selling_price: parseFloat(form.selling_price),
          barcode: form.barcode || null,
          imeis: imeisArray ? JSON.stringify(imeisArray) : null,
          quantity: effectiveQuantity,
        };
        await updateItem(form.item_id, updateItemPayload);
      }

      const payload = {
        item_id: itemId,
        branch_id: form.branch_id,
        quantity: effectiveQuantity,
        buying_price: parseFloat(form.buying_price),
        selling_price: parseFloat(form.selling_price),
        supplier_id: form.supplier_id,
        // Invoice options
        invoice_action: form.invoice_action,
        custom_invoice_number: form.custom_invoice_number || null,
        existing_invoice_id: form.existing_invoice_id || null,
      };

      let invId: string;
      let response: any;
      if (editing) {
        response = await updateInventory(editing.id, payload);
        invId = editing.id;
        toast({ title: 'Updated', description: 'Inventory updated' });
      } else {
        response = await createInventory(payload);
        invId = response.data.inventory?.id || response.data.id;
        
        // Handle different invoice scenarios
        const supplier = suppliers.find(s => s.id === form.supplier_id);
        const supplierName = supplier?.name || 'Unknown Supplier';
        
        if (response.data.invoice_created && response.data.invoice) {
          // New invoice was created
          const invoiceNumber = response.data.invoice.invoice_number;
          const totalAmount = response.data.invoice.amount;
          
          toast({ 
            title: 'Success!', 
            description: (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Inventory added successfully</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  New invoice {invoiceNumber} created for {supplierName} (${totalAmount})
                </div>
              </div>
            ),
            duration: 6000
          });
        } else if (response.data.invoice_updated && response.data.invoice) {
          // Existing invoice was updated
          const invoiceNumber = response.data.invoice.invoice_number;
          const totalAmount = response.data.invoice.amount;
          
          toast({ 
            title: 'Success!', 
            description: (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Inventory added successfully</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Invoice {invoiceNumber} updated for {supplierName} (${totalAmount})
                </div>
              </div>
            ),
            duration: 6000
          });
        } else {
          toast({ title: 'Added', description: 'New inventory record created' });
        }
      }

      const [invRes, itemsRes] = await Promise.all([
        fetchInventory(),
        fetchItems(),
      ]);

      const filteredInventory = isAdmin
        ? invRes.data
        : invRes.data.filter((inv: InventoryRaw) => inv.branch_id === userBranchId);

      setRawInventory(filteredInventory);
      setItems(itemsRes.data.map(transformItem));
      setInventory(enrich(filteredInventory, itemsRes.data));
      setDialogOpen(false);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const onDelete = async (id: string) => {
    if (!isAdmin) return;
    try {
      await deleteInventory(id);
      toast({ title: 'Deleted', description: 'Inventory removed' });
      const [invRes, itemsRes] = await Promise.all([
        fetchInventory(),
        fetchItems(),
      ]);

      const filteredInventory = isAdmin
        ? invRes.data
        : invRes.data.filter((inv: InventoryRaw) => inv.branch_id === userBranchId);

      setRawInventory(filteredInventory);
      setItems(itemsRes.data.map(transformItem));
      setInventory(enrich(filteredInventory, itemsRes.data));
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold">Inventory</h1>
        <p className="text-muted-foreground">Track and manage stock by branch. Supplier invoices are automatically created when adding inventory.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex gap-2 items-center">
              <Package className="h-5 w-5 text-primary" />
              <CardTitle>Inventory Items</CardTitle>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setTransferDialogOpen(true)}
                >
                  <ArrowRightLeft className="h-4 w-4 mr-1" /> Inventory Transfer
                </Button>
                <Button onClick={() => openDialog()}>
                  <Plus className="h-4 w-4 mr-1" /> Add Inventory
                </Button>
              </div>
            )}
          </div>
          <CardDescription>Filter by status or search by name/SKU/barcode. Adding inventory automatically generates supplier invoices.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            {isAdmin && (
              <Select
                value={branchFilter}
                onValueChange={v => setBranchFilter(v as 'all' | string)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {([
              { value: 'all', label: 'All' },
              { value: 'in-stock', label: 'In Stock' },
              { value: 'low-stock', label: 'Low Stock' },
              { value: 'out-of-stock', label: 'Out of Stock' },
            ] as const).map(s => (
              <Button
                key={s.value}
                variant={filterStatus === s.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(s.value)}
              >
                {s.label}
              </Button>
            ))}

            <div className="relative flex-1 max-w-xs">
              <Search className="absolute top-2 left-2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search name, SKU, or barcode…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>IMEIs</TableHead>
                  <TableHead>Branch</TableHead>
                  {isAdmin && <TableHead>Supplier</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Quantity</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.sku}</TableCell>
                    <TableCell>{r.barcode || '—'}</TableCell>
                    <TableCell>{r.imeis ? JSON.parse(r.imeis).join(', ') : '—'}</TableCell>
                    <TableCell>{branches.find(b => b.id === r.branch_id)?.name || '—'}</TableCell>
                    {isAdmin && (
                      <TableCell>{suppliers.find(s => s.id === r.supplier_id)?.name || '—'}</TableCell>
                    )}
                    <TableCell>
                      <Badge
                        variant={
                          r.status === 'In Stock' ? 'default' :
                          r.status === 'Low Stock' ? 'secondary' :
                          'destructive'
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.available_quantity !== undefined ? (
                        <div className="flex flex-col">
                          <span>{r.available_quantity} available</span>
                          {r.reserved_quantity > 0 && (
                            <span className="text-xs text-orange-600">
                              ({r.reserved_quantity} reserved)
                            </span>
                          )}
                        </div>
                      ) : (
                        r.quantity
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDialog(r)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete(r.id)}
                          >
                            ❌
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-w-full max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editing ? 'Edit Inventory' : 'Add New Inventory'}
              </DialogTitle>
              <DialogDescription>
                {editing 
                  ? 'Edit an existing inventory record.' 
                  : 'Add a new inventory record. A supplier invoice will be automatically created based on the buying price and quantity.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 py-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="custom-item"
                  checked={form.isCustomItem}
                  onCheckedChange={(checked) => setForm(f => ({ ...f, isCustomItem: !!checked, item_id: '' }))}
                  className="h-4 w-4"
                />
                <label htmlFor="custom-item" className="text-sm font-medium text-gray-700">
                  Add Custom Item
                </label>
              </div>

              {form.isCustomItem ? (
                <>
                  <div>
                    <label className="text-sm font-medium block mb-1">Item Name</label>
                    <Input
                      value={form.item_name}
                      onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
                      placeholder="Enter item name"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">SKU</label>
                    <Input
                      value={form.item_sku}
                      onChange={e => setForm(f => ({ ...f, item_sku: e.target.value }))}
                      placeholder="Enter SKU"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Barcode</label>
                    <Input
                      value={form.barcode}
                      onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
                      placeholder="Enter barcode (optional)"
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="has-imei-custom"
                      checked={form.hasImei}
                      onCheckedChange={(checked) => setForm(f => ({
                        ...f,
                        hasImei: !!checked,
                        imeis: checked ? f.imeis : '',
                        quantity: checked ? '1' : f.quantity,
                      }))}
                      className="h-4 w-4"
                    />
                    <label htmlFor="has-imei-custom" className="text-sm font-medium text-gray-700">
                      Enable IMEI
                    </label>
                  </div>
                  {form.hasImei && (
                    <div>
                      <label className="text-sm font-medium block mb-1">IMEIs (comma-separated)</label>
                      <Input
                        value={form.imeis}
                        onChange={e => setForm(f => ({ ...f, imeis: e.target.value }))}
                        placeholder="Enter IMEIs, e.g., 123456789012345, 987654321098765"
                        className="w-full"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium block mb-1">Quantity</label>
                    <Input
                      type="number"
                      value={form.hasImei ? '1' : form.quantity}
                      onChange={e => !form.hasImei && setForm(f => ({ ...f, quantity: e.target.value }))}
                      disabled={form.hasImei}
                      required
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="vat-enabled-custom"
                      checked={form.vat_enabled}
                      onCheckedChange={(checked) => setForm(f => ({ ...f, vat_enabled: !!checked }))}
                      className="h-4 w-4"
                    />
                    <label htmlFor="vat-enabled-custom" className="text-sm font-medium text-gray-700">
                      Enable VAT for this item
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium block mb-1">Item</label>
                    <Command className="w-full border rounded-md" shouldFilter={false}>
                      <CommandInput
                        placeholder="Search item…"
                        value={itemSearchQuery}
                        onValueChange={setItemSearchQuery}
                        className="w-full"
                      />
                      <CommandList className="max-h-60 overflow-auto">
                        <CommandEmpty>No items found</CommandEmpty>
                        {items
                          .filter(i =>
                            i.name.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                            i.sku.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                            (i.barcode && i.barcode.toLowerCase().includes(itemSearchQuery.toLowerCase()))
                          )
                          .map(i => (
                            <CommandItem
                              key={i.id}
                              value={i.id}
                              onSelect={val => {
                                setForm(f => ({ ...f, item_id: val }));
                                setItemSearchQuery('');
                              }}
                            >
                              {i.name} — <span className="text-xs text-muted-foreground">{i.sku}</span>
                            </CommandItem>
                          ))}
                      </CommandList>
                    </Command>
                  </div>
                </>
              )}

              {selectedItem && !form.isCustomItem && (
                <div className="space-y-2 p-2 bg-gray-50 rounded-md">
                  <p className="text-sm font-medium">Selected Item: {selectedItem.name} (SKU: {selectedItem.sku})</p>
                  <p className="text-sm text-muted-foreground">
                    Current Buying Price: ${selectedItem.buying_price.toFixed(2)} | 
                    Current Selling Price: ${selectedItem.selling_price.toFixed(2)}
                  </p>
                  {selectedItem.barcode && (
                    <p className="text-sm text-muted-foreground">Barcode: {selectedItem.barcode}</p>
                  )}
                  {selectedItem.imeis && (
                    <p className="text-sm text-muted-foreground">IMEIs: {JSON.parse(selectedItem.imeis).join(', ')}</p>
                  )}
                  {selectedItem.vat_enabled === 1 && (
                    <p className="text-sm text-muted-foreground">
                      VAT Enabled: Yes (VAT Rate: {vatPercentage}%)
                    </p>
                  )}
                </div>
              )}

              {!form.isCustomItem && (
                <>
                  <div>
                    <label className="text-sm font-medium block mb-1">Barcode</label>
                    <Input
                      value={form.barcode}
                      onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
                      placeholder="Enter barcode (optional)"
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="has-imei-existing"
                      checked={form.hasImei}
                      onCheckedChange={(checked) => setForm(f => ({
                        ...f,
                        hasImei: !!checked,
                        imeis: checked ? f.imeis : '',
                        quantity: checked ? '1' : f.quantity,
                      }))}
                      className="h-4 w-4"
                    />
                    <label htmlFor="has-imei-existing" className="text-sm font-medium text-gray-700">
                      Enable IMEI
                    </label>
                  </div>
                  {form.hasImei && (
                    <div>
                      <label className="text-sm font-medium block mb-1">IMEIs (comma-separated)</label>
                      <Input
                        value={form.imeis}
                        onChange={e => setForm(f => ({ ...f, imeis: e.target.value }))}
                        placeholder="Enter IMEIs, e.g., 123456789012345, 987654321098765"
                        className="w-full"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium block mb-1">Quantity</label>
                    <Input
                      type="number"
                      value={form.hasImei ? '1' : form.quantity}
                      onChange={e => !form.hasImei && setForm(f => ({ ...f, quantity: e.target.value }))}
                      disabled={form.hasImei}
                      required
                      className="w-full"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="text-sm font-medium block mb-1">Branch</label>
                <Select
                  value={form.branch_id}
                  onValueChange={val => setForm(f => ({ ...f, branch_id: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select branch…" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Supplier</label>
                <Select
                  value={form.supplier_id}
                  onValueChange={val => setForm(f => ({ ...f, supplier_id: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select supplier…" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Invoice Options */}
              {!editing && form.buying_price && form.quantity && form.supplier_id && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Invoice Options</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium block mb-2">What would you like to do?</label>
                      <Select
                        value={form.invoice_action}
                        onValueChange={val => setForm(f => ({ ...f, invoice_action: val, existing_invoice_id: '' }))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="create">Create New Invoice</SelectItem>
                          <SelectItem value="add_to_existing">Add to Existing Invoice</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.invoice_action === 'create' && (
                      <div>
                        <label className="text-sm font-medium block mb-2">Invoice Number (Optional)</label>
                        <Input
                          value={form.custom_invoice_number}
                          onChange={e => setForm(f => ({ ...f, custom_invoice_number: e.target.value }))}
                          placeholder="Leave empty for auto-generation"
                          className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {form.custom_invoice_number 
                            ? `Will generate: XXX-${form.custom_invoice_number}` 
                            : 'Will auto-generate: XXX-INV-YYYYMMDD-XXXX'
                          }
                        </div>
                      </div>
                    )}

                    {form.invoice_action === 'add_to_existing' && (
                      <div>
                        <label className="text-sm font-medium block mb-2">Select Existing Invoice</label>
                        {supplierInvoices.length > 0 ? (
                          <Select
                            value={form.existing_invoice_id}
                            onValueChange={val => setForm(f => ({ ...f, existing_invoice_id: val }))}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select an invoice..." />
                            </SelectTrigger>
                            <SelectContent>
                              {supplierInvoices.map(invoice => (
                                <SelectItem key={invoice.id} value={invoice.id}>
                                  {invoice.invoiceNumber} - ${invoice.amount.toFixed(2)} ({invoice.date})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm text-gray-500 p-3 bg-gray-100 rounded">
                            No pending invoices found for this supplier.
                          </div>
                        )}
                      </div>
                    )}

                    <div className="text-sm text-blue-700 bg-blue-100 p-2 rounded">
                      <strong>Item Cost: ${(parseFloat(form.buying_price || '0') * parseInt(form.quantity || '0', 10)).toFixed(2)}</strong>
                      <br />
                      Supplier: {suppliers.find(s => s.id === form.supplier_id)?.name || 'Selected Supplier'}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Buying Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.buying_price}
                    onChange={e => setForm(f => ({ ...f, buying_price: e.target.value }))}
                    required
                    className="w-full"
                  />
                  {avgCost > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      className="mt-1 mr-1 w-full"
                      onClick={() => setForm(f => ({ ...f, buying_price: avgCost.toFixed(2) }))}
                    >
                      Use avg cost ($ {avgCostForButton.toFixed(2)})
                    </Button>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium block mb-1">Selling Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.selling_price}
                    onChange={e => setForm(f => ({ ...f, selling_price: e.target.value }))}
                    required
                    className="w-full"
                  />
                  {(form.isCustomItem ? form.vat_enabled : selectedItem?.vat_enabled === 1) && form.selling_price && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Selling Price (incl. VAT {vatPercentage}%): ${sellingPriceWithVat}
                    </p>
                  )}
                </div>
              </div>

              <Button type="submit" className="w-full">
                {editing ? 'Update' : 'Add'} Inventory
                {!editing && form.buying_price && form.quantity && ' + Generate Invoice'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Inventory Transfer</DialogTitle>
            <DialogDescription>
              Transfer inventory items between branches
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransferSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">From Branch</label>
              <Popover 
                open={transferPopovers.fromBranch} 
                onOpenChange={open => setTransferPopovers(prev => ({ ...prev, fromBranch: open }))}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={transferPopovers.fromBranch}
                    className="w-full justify-between"
                  >
                    {transferForm.fromBranch 
                      ? branches.find(branch => branch.id === transferForm.fromBranch)?.name
                      : "Select source branch"
                    }
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search branches..." />
                    <CommandList>
                      <CommandEmpty>No branch found.</CommandEmpty>
                      {branches.map(branch => (
                        <CommandItem
                          key={branch.id}
                          value={branch.name}
                          onSelect={() => {
                            setTransferForm(prev => ({ ...prev, fromBranch: branch.id, toBranch: '', itemId: '', quantity: '' }));
                            setTransferPopovers(prev => ({ ...prev, fromBranch: false }));
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${transferForm.fromBranch === branch.id ? "opacity-100" : "opacity-0"}`} />
                          {branch.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium">To Branch</label>
              <Popover 
                open={transferPopovers.toBranch} 
                onOpenChange={open => setTransferPopovers(prev => ({ ...prev, toBranch: open }))}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={transferPopovers.toBranch}
                    className="w-full justify-between"
                  >
                    {transferForm.toBranch 
                      ? branches.find(branch => branch.id === transferForm.toBranch)?.name
                      : "Select destination branch"
                    }
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search branches..." />
                    <CommandList>
                      <CommandEmpty>No branch found.</CommandEmpty>
                      {branches.filter(b => b.id !== transferForm.fromBranch).map(branch => (
                        <CommandItem
                          key={branch.id}
                          value={branch.name}
                          onSelect={() => {
                            setTransferForm(prev => ({ ...prev, toBranch: branch.id }));
                            setTransferPopovers(prev => ({ ...prev, toBranch: false }));
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${transferForm.toBranch === branch.id ? "opacity-100" : "opacity-0"}`} />
                          {branch.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium">Item</label>
              <Popover 
                open={transferPopovers.item} 
                onOpenChange={open => setTransferPopovers(prev => ({ ...prev, item: open }))}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={transferPopovers.item}
                    className="w-full justify-between"
                    disabled={!transferForm.fromBranch}
                  >
                    {transferForm.itemId 
                      ? (() => {
                          const selectedItem = getAvailableItemsForTransfer().find(item => item.item_id === transferForm.itemId);
                          return selectedItem ? `${selectedItem.name} (${selectedItem.sku}) - Available: ${selectedItem.quantity}` : "Select item to transfer";
                        })()
                      : "Select item to transfer"
                    }
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search items..." />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      {getAvailableItemsForTransfer().map(item => (
                        <CommandItem
                          key={item.id}
                          value={`${item.name} ${item.sku}`}
                          onSelect={() => {
                            setTransferForm(prev => ({ ...prev, itemId: item.item_id, quantity: '' }));
                            setTransferPopovers(prev => ({ ...prev, item: false }));
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${transferForm.itemId === item.item_id ? "opacity-100" : "opacity-0"}`} />
                          {item.name} ({item.sku}) - Available: {item.quantity}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {transferForm.itemId && (
              <div>
                <label className="text-sm font-medium">
                  Transfer Quantity 
                  <span className="text-muted-foreground ml-1">
                    (Available: {getSelectedItemQuantity()})
                  </span>
                </label>
                <Input
                  type="number"
                  min="1"
                  max={getSelectedItemQuantity()}
                  value={transferForm.quantity}
                  onChange={e => setTransferForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="Enter quantity to transfer"
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTransferDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!transferForm.fromBranch || !transferForm.toBranch || !transferForm.itemId || !transferForm.quantity}
              >
                Transfer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
